import * as React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { IntlProvider } from "react-intl";
import { TranslatedBlockContentProvider } from "./context/TranslatedBlockContentContext";
import { translateBlockContentNodes } from "./helpers/translateBlockContentNodes";
import { mergeMessages } from "./helpers/mergeMessages";
import { messages as siteMessages } from "@site-messages";

// Merge site messages with theme defaults (done once at module load time)
const messages = mergeMessages(siteMessages);

/**
 * Derives page identifier from originalPath.
 * Examples:
 *   "/" -> "index"
 *   "/about/" -> "about"
 *   "/services/custom-software/" -> "services/custom-software"
 */
const derivePageId = (originalPath) => {
    if (!originalPath) return null;
    // Remove leading and trailing slashes
    const trimmed = originalPath.replace(/^\/|\/$/g, '');
    // Empty string means home page
    return trimmed === '' ? 'index' : trimmed;
};

/**
 * Component that fetches all block content via useStaticQuery,
 * filters by page, and provides translated content via context.
 */
const BlockContentProvider = ({ pageContext, children }) => {
    const data = useStaticQuery(graphql`
        query AllBlockContentForPages {
            allBlockContent {
                nodes {
                    ...BlockContentTranslation
                }
            }
        }
    `);

    const { locale, originalPath } = pageContext || {};

    // Filter nodes: include "shared" and page-specific content
    const pageId = derivePageId(originalPath);
    const filteredNodes = data.allBlockContent.nodes.filter(
        node => node.page === 'shared' || node.page === pageId
    );

    // Translate the filtered content (use locale if available, otherwise content stays as default)
    const translatedContent = translateBlockContentNodes(
        { allBlockContent: { nodes: filteredNodes } },
        { locale: locale || 'en' }
    );

    return (
        <TranslatedBlockContentProvider value={translatedContent}>
            {children}
        </TranslatedBlockContentProvider>
    );
};

/**
 * wrapPageElement for Gatsby.
 * Handles both UI string messages (IntlProvider) and block content translation.
 */
export const wrapPageElement = ({ element, props }) => {
    const { locale, defaultLocale } = props.pageContext || {};

    // Handle pages without locale context (e.g., 404 during build)
    if (!locale) {
        return (
            <BlockContentProvider pageContext={props.pageContext}>
                {element}
            </BlockContentProvider>
        );
    }

    // Fallback to default locale for missing message keys
    const mergedLocaleMessages = { ...messages[locale] };
    for (const key in messages[defaultLocale]) {
        if (!mergedLocaleMessages[key]) {
            mergedLocaleMessages[key] = messages[defaultLocale][key];
        }
    }

    return (
        <IntlProvider
            messages={mergedLocaleMessages}
            locale={locale}
            defaultLocale={defaultLocale}
        >
            <BlockContentProvider pageContext={props.pageContext}>
                {element}
            </BlockContentProvider>
        </IntlProvider>
    );
};